import React, { useState, useEffect } from 'react'
import { Modal, Button, Badge, Table, Spinner, Alert } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { amazonAPI } from '../api/amazonAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { AmazonAnomalyRuleSummary, ruleDisplayValues } from './AmazonAnomalyRuleSummary'

const SETTINGS_PATH = '/amazon-ad-automation/settings'

const Section = ({ title, children }) => (
    <section className="mb-4">
        <h5 className="mb-2">{title}</h5>
        {children}
    </section>
)

/**
 * "How Anomaly Detection works": a short guide for the advertising team. Rule descriptions, the
 * daily cap and the weekly minimum are read from the account's settings, so the guide matches
 * what is configured. Opened from the Anomaly Detection card and its settings tab.
 */
function AmazonAnomalyDetectionGuideModal({ show, onHide, accountId, accountName = '', linkToSettings = true }) {
    const [settings, setSettings] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!show || !accountId) return
        setLoading(true)
        setError('')
        amazonAPI.anomalySettings.get(accountId)
            .then(setSettings)
            .catch(err => setError(formatErrorResponse(err)))
            .finally(() => setLoading(false))
    }, [show, accountId])

    const cap = settings?.daily_cap ?? 15
    const minimum = settings?.min_weekly_impact ?? 25
    // Opened from the settings page itself, links would switch tabs past its unsaved-changes check
    const settingsLink = (tab, label) => (linkToSettings
        ? <Link to={`${SETTINGS_PATH}?tab=${tab}${accountId ? `&account=${accountId}` : ''}`} onClick={onHide}>{label}</Link>
        : <strong>{label}</strong>)

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={false} scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    How Anomaly Detection works
                    {accountName && <div className="small text-body-secondary fw-normal">Values shown for {accountName}</div>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Section title="What it does">
                    <p className="mb-2">
                        Every day it checks every advertised product and puts only the exceptions on the
                        <strong> Needs Attention</strong> list, most money at stake first. The goal is a list you can
                        clear in about ten minutes.
                    </p>
                    <ul className="mb-0">
                        <li>Uses only data already collected each night (ads, sales, traffic, fees, COGS, FBA stock). It makes no Amazon calls.</li>
                        <li><strong>Watch only:</strong> it never changes bids, budgets or campaigns.</li>
                        <li>Runs once a day per account at the time set in {settingsLink('general', 'Settings → General')}, and anytime with <strong>Run</strong> on its card.</li>
                    </ul>
                </Section>

                <Section title="Reading an alert">
                    <ul className="mb-0">
                        <li><strong>Severity:</strong> <Badge bg="danger">Critical</Badge> <Badge bg="warning" text="dark">Warning</Badge> <Badge bg="info" text="dark">Info</Badge>. Info items stay off the daily list and appear in the weekly summary.</li>
                        <li><strong>Impact:</strong> money at stake <em>per week</em>, an estimate of size used for sorting.</li>
                        <li><strong>Details</strong> opens the evidence (Daily Dashboard or SP Campaigns) filtered to the product.</li>
                        <li><strong>Also seen on this product:</strong> other symptoms of the same root cause; only the cause becomes the alert.</li>
                        <li>An <strong>Exception</strong> badge means the product has its own target ACoS or is excluded.</li>
                    </ul>
                </Section>

                <Section title="Handling an alert">
                    <Table size="sm" className="mb-0">
                        <tbody>
                            <tr><td className="fw-semibold text-nowrap">Dismiss</td><td>Seen it. Optional reason: <em>Acted on it</em> or <em>Seen, leave as is</em>. Stays dismissed while the problem lasts.</td></tr>
                            <tr><td className="fw-semibold text-nowrap">Not useful</td><td>Wrong or not worth showing. Pick a reason and add a comment: this feedback is used to tune the rules.</td></tr>
                            <tr><td className="fw-semibold text-nowrap">Auto-closed</td><td>The problem went away by itself. It reopens if it comes back.</td></tr>
                            <tr><td className="fw-semibold text-nowrap">Product exception…</td><td>In the row&apos;s <strong>⋯</strong> menu: give the product a target ACoS, or exclude it from Anomaly Detection.</td></tr>
                        </tbody>
                    </Table>
                </Section>

                <Section title="Ground rules">
                    <ul className="mb-0">
                        <li><strong>Only complete data.</strong> Spend and clicks are final after about 2 days; ad sales and orders keep arriving for about 7, so sales rules judge only days at least 7 days old.</li>
                        <li><strong>Enough data.</strong> Checks add up 14 days and need a minimum number of clicks, so one lucky or unlucky day decides nothing.</li>
                        <li><strong>Each product against itself.</strong> Its own break-even ACoS (or target ACoS) and its own usual conversion rate, never one company-wide target.</li>
                        <li><strong>Launch grace.</strong> Products in their first 45 days of ad spend are skipped.</li>
                        <li><strong>Nothing closes by accident.</strong> If data is stale or a rule could not run, its alerts stay open; the card shows <em>skipped</em> or <em>Partly skipped</em> with the reason.</li>
                    </ul>
                </Section>

                <Section title="How findings become alerts">
                    <ol className="mb-0">
                        <li><strong>Skip</strong> excluded products and products in launch grace.</li>
                        <li><strong>Confirm:</strong> ratio-based problems must be seen again on a later day.</li>
                        <li><strong>Group:</strong> when most products move at once (a holiday, Prime, an outage), one account alert replaces the product alerts.</li>
                        <li><strong>One per product:</strong> only the root cause is shown (data health → account → stock → price and fees → demand → ads).</li>
                        <li><strong>Keep it short:</strong> at most <strong>{cap}</strong> critical and warning alerts per day; problems under <strong>${minimum}</strong> a week become info.</li>
                        <li><strong>Write:</strong> new problems open, known ones update, gone ones auto-close.</li>
                    </ol>
                </Section>

                <Section title="The rules">
                    {loading && <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>}
                    {error && <Alert variant="warning" className="mb-2">Could not load this account&apos;s rule settings: {error}</Alert>}
                    {settings && settings.rules.map(rule => (
                        <div key={rule.key} className="mb-3">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <strong>{rule.label}</strong>
                                <span className="small text-body-secondary">{rule.family_label}</span>
                                {!rule.enabled && <Badge bg="secondary">Off for this account</Badge>}
                            </div>
                            <AmazonAnomalyRuleSummary rule={rule} displayValues={ruleDisplayValues(rule)} className="small" />
                        </div>
                    ))}
                </Section>

                <Section title="Key terms">
                    <Table size="sm" className="mb-0">
                        <tbody>
                            <tr><td className="fw-semibold text-nowrap">ACoS</td><td>Ad spend ÷ ad sales. $30 of ads brought $100 of sales = 30%.</td></tr>
                            <tr><td className="fw-semibold text-nowrap">Break-even ACoS</td><td>(Price − COGS − Amazon fees) ÷ price: the most ads may cost before each sale loses money. The same number as the Daily Dashboard.</td></tr>
                            <tr><td className="fw-semibold text-nowrap">Target ACoS</td><td>Set per product by the team; replaces break-even for that product.</td></tr>
                            <tr><td className="fw-semibold text-nowrap">Conversion rate</td><td>Orders ÷ clicks. &quot;Usual&quot; = over 8 earlier weeks, pulled toward the account average when history is thin.</td></tr>
                        </tbody>
                    </Table>
                </Section>

                <Section title="Where to change things">
                    <ul className="mb-0">
                        <li>{settingsLink('anomaly', 'Settings → Anomaly Detection')}: daily cap, weekly minimum, each rule on or off, thresholds.</li>
                        <li>{settingsLink('products', 'Settings → Product exceptions')}: target ACoS or exclusion per product.</li>
                        <li>{settingsLink('general', 'Settings → General')}: when the daily run happens.</li>
                    </ul>
                    <div className="small text-body-secondary mt-2">
                        Viewing needs Amazon access; changing needs Amazon manage access. Changes apply from the next run.
                    </div>
                </Section>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AmazonAnomalyDetectionGuideModal
